import React, { useEffect, useMemo, useState } from 'react';
import EditButton from '../../../ui/EditButton';
import { collection } from 'firebase/firestore';
import { db } from '../../../../firebase-client';
import { safeOnSnapshot } from '../../../../utils/firestoreSubscriptions';
import BoatCalendarModal from '../../components/modals/rowing/BoatCalendarModal';
import { CalendarButton } from '../../../ui/CalendarButton';
import { isRowingFleetEnabled } from '../../utils/moduleUtils';
function toFiniteNumberOrNull(value) {
  if (value === '' || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}
function crewWeightLabel(boat) {
  const minRaw = toFiniteNumberOrNull(boat?.crewWeightMinKg);
  const maxRaw = toFiniteNumberOrNull(boat?.crewWeightMaxKg);
  const min = minRaw !== null && maxRaw !== null && minRaw > maxRaw ? maxRaw : minRaw;
  const max = minRaw !== null && maxRaw !== null && minRaw > maxRaw ? minRaw : maxRaw;
  if (min !== null && max !== null) return `Crew ${min}-${max}kg`;
  if (min !== null) return `Crew ${min}+kg`;
  if (max !== null) return `Crew <=${max}kg`;
  return '';
}
function compareBoatsByTypeThenName(a, b) {
  const typeCompare = (a?.type || '').localeCompare(b?.type || '', undefined, {
    sensitivity: 'base'
  });
  if (typeCompare !== 0) return typeCompare;
  return (a?.name || '').localeCompare(b?.name || '', undefined, {
    sensitivity: 'base'
  });
}
export default function RowingBoatsCard({
  isAdmin,
  teamId,
  modules = {},
  onEdit,
  onEditBoat,
  canManageMaintenance = false,
  onCreateMaintenanceTicket,
}) {
  const enabled = isRowingFleetEnabled(modules);
  const showCard = isAdmin && enabled;
  const [boats, setBoats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [page, setPage] = useState(1);
  const pageSize = 4;

  // modal state
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [focusedBoatId, setFocusedBoatId] = useState(null);
  useEffect(() => {
    if (!showCard || !teamId) return undefined;
    setLoading(true);
    const unsub = safeOnSnapshot(collection(db, `teams/${teamId}/boats`), snap => {
      const list = snap.docs.map(d => ({
        id: d.id,
        ...d.data()
      })).sort(compareBoatsByTypeThenName);
      setBoats(list);
      setLoading(false);
    }, () => {
      setLoading(false);
    }, { label: 'rowing-fleet', teamId });
    return () => unsub();
  }, [showCard, teamId]);

  const total = boats.length;
  const active = useMemo(() => boats.filter(b => (b?.status || 'active') !== 'maintenance').length, [boats]);
  const maintenance = total - active;
  const byType = useMemo(() => boats.reduce((acc, r) => {
    const k = r?.type || 'other';
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {}), [boats]);
  const typeOptions = useMemo(() => {
    const types = Array.from(new Set(boats.map(b => b?.type || 'other').filter(Boolean)));
    return types.sort((a, b) => a.localeCompare(b, undefined, {
      sensitivity: 'base'
    }));
  }, [boats]);
  const filteredBoats = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return boats.filter(b => {
      const type = b?.type || 'other';
      if (typeFilter !== 'all' && type !== typeFilter) return false;
      if (!term) return true;
      const hay = `${b?.name || ''} ${type} ${b?.crewWeightMinKg || ''} ${b?.crewWeightMaxKg || ''} ${b?.registrationNumber || ''}`.toLowerCase();
      return hay.includes(term);
    }).sort(compareBoatsByTypeThenName);
  }, [boats, searchTerm, typeFilter]);
  const totalPages = Math.max(1, Math.ceil(filteredBoats.length / pageSize));
  const pagedBoats = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredBoats.slice(start, start + pageSize);
  }, [filteredBoats, page, pageSize]);
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);
  if (!showCard) return null;
  return <div className="team-card rowing-boats-card">
      <div className="card-header-with-icon" style={{
      display: 'flex',
      alignItems: 'center',
      gap: 8
    }}>
        <span className="card-title" style={{
        flex: 1
      }}>Boats (Admin)</span>

        <EditButton section="rowingBoats" onEdit={onEdit} />
      </div>

      <div className="card-body">
        <div className="availability-status-list rowing-boats-card__summary">
          <div className="availability-status-item">
            <div className="availability-status-label">
              <span className="availability-status-icon">🛶</span>
              <span>Fleet</span>
            </div>

            {(() => {
            const short = loading ? '…' : `${active}/${total}`;
            const tip = loading ? 'Loading…' : `${active} active, ${maintenance} maintenance (total ${total})`;
            return <span className={`availability-pill ${active ? 'available' : 'unavailable'}`} title={tip} style={{
              padding: '0.2rem 0.5rem',
              // tighter than default
              fontSize: '0.75rem',
              // a touch smaller
              whiteSpace: 'nowrap' // prevent wrapping
            }}>
                  {short}
                </span>;
          })()}
          </div>

          {!!total && <div className="availability-status-item">
              <div className="availability-status-label">
                <span className="availability-status-icon">🏷️</span>
                <span>By Type</span>
              </div>
              <span
                className="availability-pill available rowing-boats-card__type-summary"
                title={Object.entries(byType).map(([t, c]) => `${t}: ${c}`).join(', ')}
              >
                {Object.entries(byType).map(([t, c]) => `${t}:${c}`).join(' · ')}
              </span>
            </div>}
        </div>

        <div className="rowing-boats-card__calendar-action">
          <CalendarButton compact onClick={() => {
          setFocusedBoatId(null);
          setCalendarOpen(true);
        }} title="Open calendar for all boats" aria-label="Open calendar for all boats">
            Open calendar
          </CalendarButton>
        </div>

        {!loading && <>
            <div className="rowing-boats-card__filters">
              <input className="input" placeholder="Search name, type, weight, reg" value={searchTerm} onChange={e => {
            setSearchTerm(e.target.value);
            setPage(1);
          }} />
              <select className="input" value={typeFilter} onChange={e => {
            setTypeFilter(e.target.value);
            setPage(1);
          }}>
                <option value="all">All types</option>
                {typeOptions.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>

            {filteredBoats.length === 0 && <div className="text-muted" style={{
          marginTop: 8
        }}>
                No boats match that search.
              </div>}

            <div className="rowing-boats-card__list">
            {pagedBoats.map(r => {
          const crewLabel = crewWeightLabel(r);
          const metadata = [
            r.type || 'Boat',
            `${r.seats ?? '—'} seats`,
            r.status === 'maintenance' ? 'Maintenance' : 'Active',
            crewLabel,
            r?.registrationNumber ? `Reg ${r.registrationNumber}` : '',
            Array.isArray(r.tagsAllowed) && r.tagsAllowed.length > 0 ? r.tagsAllowed.join(', ') : '',
          ].filter(Boolean);
          return <div key={r.id} className="list-row rowing-boats-card__boat-row">
                  <div className="rowing-boats-card__boat-details">
                    <strong>{r.name || 'Unnamed'}</strong>
                    <span>{metadata.join(' · ')}</span>
                  </div>
                  <div className="rowing-boats-card__boat-actions">
                  {onEditBoat && <button type="button" className="btn btn--secondary btn--xs" onClick={() => onEditBoat(r)} title={`Edit ${r.name || 'this boat'}`} aria-label={`Edit ${r.name || 'this boat'}`}>
                      <span aria-hidden="true">✏️</span>
                      <span className="rowing-boats-card__action-label">Edit</span>
                    </button>}
                  {canManageMaintenance && onCreateMaintenanceTicket && <button type="button" className="btn btn--secondary btn--xs" onClick={() => onCreateMaintenanceTicket(r)} title={`Create a maintenance ticket for ${r.name || 'this boat'}`} aria-label={`Create a maintenance ticket for ${r.name || 'this boat'}`}>
                      <span aria-hidden="true">🔧</span>
                      <span className="rowing-boats-card__action-label">Maintenance ticket</span>
                    </button>}
                  <CalendarButton compact iconOnly onClick={() => {
              setFocusedBoatId(r.id);
              setCalendarOpen(true);
            }} title={`Open ${r.name || 'this boat'} calendar`} aria-label={`Open ${r.name || 'this boat'} calendar`}>
                    Calendar
                  </CalendarButton>
                  </div>
                </div>;
        })}
            </div>

            {filteredBoats.length > 0 && <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: 10,
          gap: 8
        }}>
                <span className="text-muted" style={{
            fontSize: '0.85rem'
          }}>
                  Page {page} of {totalPages}
                </span>
                <div style={{
            display: 'flex',
            gap: 6
          }}>
                  <button type="button" className="btn btn--secondary btn--xs" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>
                    Prev
                  </button>
                  <button type="button" className="btn btn--secondary btn--xs" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>
                    Next
                  </button>
                </div>
              </div>}
          </>}
      </div>

      {/* Modal mount */}
      {calendarOpen && <BoatCalendarModal isOpen={calendarOpen} onClose={() => setCalendarOpen(false)} teamId={teamId} boats={boats} selectedBoatId={focusedBoatId} // null => show all boats by default
    />}
    </div>;
}
